import { DialogTitle } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import Modal from '@/components/common/modal/modal';
import useUserPlatform from '@/hooks/use-user-platform';
import { usePrompterStore } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

export default function AppPrompter() {
  const { t } = useTranslation();
  const prompt = usePrompterStore(useShallow((s) => Object.values(s.prompts).at(0)));
  const availableButtonCount = prompt?.resolutionButtons?.filter((button) => !button.hidden)?.length ?? 0;
  const { isEmbedded } = useUserPlatform();
  return (
    <div id='app-prompter'>
      {prompt && (
        <Modal
          key={prompt.key}
          defaultOpen
          clickOutsideToClose={prompt.resolveButtonType !== 'informative'}
          onClose={() => prompt.resolvers.resolve(false)}>
          <DialogTitle className='my-4 text-base font-bold'>{prompt.title}</DialogTitle>

          {typeof prompt.content === 'string' ? <p className='text-secondary'>{prompt.content}</p> : prompt.content}

          <div
            className={cn('mb-4 mt-6 flex xs:items-center items-end justify-end gap-2', {
              'xs:flex-row flex-wrap': availableButtonCount > 1,
            })}>
            {prompt.resolutionButtons
              ?.filter((button) => !button.hidden)
              .map((button) => (
                <button
                  key={button.resolution}
                  type='button'
                  disabled={prompt.isLoading}
                  className={cn('btn h-10 min-h-10  btn-primary w-[8.25rem] font-normal', {
                    'btn-error': button.type === 'destructive',
                    'rounded-full': isEmbedded,
                  })}
                  onClick={() => prompt.resolvers.resolve(button.resolution)}>
                  {prompt.isLoading ? <span className='loading loading-spinner loading-sm' /> : button.label}
                </button>
              )) ?? (
              <button
                data-testid='prompter-confirm-btn'
                type='button'
                disabled={prompt.isLoading}
                className={cn('btn h-10 min-h-10 font-normal btn-primary', {
                  'btn-error': prompt.resolveButtonType === 'destructive',
                  'rounded-full': isEmbedded,
                })}
                onClick={() => prompt.resolvers.resolve(true)}>
                {prompt.isLoading ? (
                  <span className='loading loading-spinner loading-sm' />
                ) : (
                  (prompt.resolveButtonLabel ?? t('common.confirm'))
                )}
              </button>
            )}

            {prompt.resolveButtonType !== 'informative' && (
              <button
                type='button'
                disabled={prompt.isLoading}
                className={cn('btn font-normal btn-ghost order-first', {
                  'order-last': availableButtonCount > 1,
                  'rounded-full': isEmbedded,
                })}
                onClick={() => prompt.resolvers.resolve(false)}>
                {prompt.rejectButtonLabel ?? t('common.cancel')}
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
